import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { bool, func, oneOfType, string, node } from "prop-types";
import cx from "classnames";

// https://hackernoon.com/its-a-focus-trap-699a04d66fb5
import FocusLock from "react-focus-lock";

import Icon from "../Icon/Icon.jsx";

const propTypes = {
  title: node,
  isOpen: bool.isRequired,
  noPadding: bool,
  onClose: func.isRequired,
  className: string,
  lang: string,
  children: oneOfType([node, func]),
  showPrintButton: bool,
};

const Modal = (props) => {
  const {
    isOpen,
    lang,
    onClose,
    title,
    children,
    className,
    noPadding,
    showPrintButton = false,
    ...rest
  } = props;

  const overflowRef = useRef();

  // keep around the children so when we close, we can fade out with the content
  const childrenCache = useRef();

  const [modalContainer, setModalContainer] = useState();
  useEffect(() => {
    const container = document.createElement("div");
    container.setAttribute("class", "modal-container");
    document.querySelector("body").appendChild(container);

    setModalContainer(container);
    return () => {
      container.remove();
    };
  }, []);

  // if children is a funciton, render it with some state
  const content =
    typeof children === "function" ? children({ isOpen, title }) : children;

  if (content) {
    childrenCache.current = content;
  }

  // listen for esc if the modal is open
  useEffect(() => {
    const keydownHandler = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      // always open with the modal scrolled to the top
      if (overflowRef.current) {
        overflowRef.current.scrollTo(0, 0);
      }

      window.addEventListener("keydown", keydownHandler);
      return () => window.removeEventListener("keydown", keydownHandler);
    } else {
      window.removeEventListener("keydown", keydownHandler);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    document.querySelector("body").classList.toggle("modal-open", isOpen);
    if (document.querySelector(".js-page-content-wrapper")) {
      document
        .querySelector(".js-page-content-wrapper")
        .setAttribute("aria-hidden", isOpen);
    }
  }, [isOpen]);

  const classes = cx("modal", className, {
    "is-open": isOpen,
    "modal--no-padding": noPadding,
  });

  const modalNode = (
    <div className={classes} ref={overflowRef} lang={lang} {...rest}>
      <div className="modal__box-holder">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div className="modal__overlay" onClick={onClose} />
        <FocusLock
          disabled={!isOpen}
          returnFocus={true}
          className="modal__box"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal__title">
            {title}
            <button
              type="button"
              className="modal__close"
              onClick={onClose}
              aria-label="Close modal"
            >
              <Icon iconName="close" />
            </button>
          </div>
          <div className="modal__content">{childrenCache.current}</div>
          {showPrintButton && (
            <div className="modal__footer">
              <button
                type="button"
                className="modal__print js-print-button"
                aria-label="Print charts"
                onClick={() => window.print()}
              >
                <Icon iconName="print" />
              </button>
            </div>
          )}
        </FocusLock>
      </div>
    </div>
  );

  return modalContainer
    ? ReactDOM.createPortal(modalNode, modalContainer)
    : null;
};

Modal.propTypes = propTypes;
export default Modal;
